/**
 * useTextStyleEditorDialog — Track 3 Phase 3
 *
 * Owns the TextStyleEditorDialog visibility + style-name resolution
 * extracted from `App.tsx` as part of the Track 3 App.tsx decomposition
 * (see `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 * Responsibilities:
 *   - Owns the `isOpen` flag and `open()` / `close()` callbacks.
 *   - Resolves the `styleName` shown in the dialog header from the
 *     current property draft, falling back to the first text style or
 *     the literal `"Standard"` (matches the original inline expression
 *     in `App.tsx`).
 *   - Wires the "Manager..." button to a caller-provided
 *     `onOpenFontManager` callback (typically the `open` function from
 *     `useFontManagerDialog`).
 *
 * Returned shape:
 *   {
 *     isOpen,
 *     props: { styleName, onClose, onOpenFontManager },
 *     open,
 *     close,
 *   }
 *
 * AutoCAD parity:
 *   AutoCAD's STYLE command opens the Text Style dialog. The dialog
 *   itself owns the combobox of style names (read from
 *   `geometryDocument.styleTables.textStyles`); the hook only resolves
 *   which entry is initially highlighted.
 */

import { useMemo } from "react";

import type { TextStyle } from "@dxf-ai-editor/drawing-model";

import { useEditorStore } from "../state/editorStore";
import { useDialogOpenState } from "./useDialogOpenState";

// Phase 1.k (П35Р React #185 fix) — stable sentinel empty array. `?? []`
// inside a Zustand selector allocates a fresh array on every store
// update, defeating Object.is bail-out → spurious re-renders.
const EMPTY_TEXT_STYLES: TextStyle[] = [];

export interface UseTextStyleEditorDialogParams {
  /**
   * Current property draft map. The hook reads `["Text style"]` to
   * pre-select the style currently applied on the selected entity.
   */
  propertyDraft: Record<string, string>;
  /**
   * Called when the user clicks the "Manager..." button inside the
   * dialog. Wired to `useFontManagerDialog.open` by App.tsx.
   */
  onOpenFontManager: () => void;
}

export interface UseTextStyleEditorDialogResult {
  /** Whether to mount `<TextStyleEditorDialog>`. */
  isOpen: boolean;
  /** Props bundle for `<TextStyleEditorDialog>`. */
  props: {
    styleName: string;
    onClose: () => void;
    onOpenFontManager: () => void;
  };
  /** Imperative open — used by quickbar / ribbon / properties view. */
  open: () => void;
  /** Imperative close — same as `props.onClose`, exposed for symmetry. */
  close: () => void;
}

export function useTextStyleEditorDialog({
  propertyDraft,
  onOpenFontManager,
}: UseTextStyleEditorDialogParams): UseTextStyleEditorDialogResult {
  const textStyles = useEditorStore(
    (store) =>
      store.geometryDocument.styleTables?.textStyles ?? EMPTY_TEXT_STYLES,
  );

  const { isOpen, open, close } = useDialogOpenState();

  // Match the original inline expression at App.tsx:5369:
  //   propertyDraft["Text style"] || textStyles[0]?.name || "Standard"
  const styleName = useMemo(
    () => propertyDraft["Text style"] || textStyles[0]?.name || "Standard",
    [propertyDraft, textStyles],
  );

  return {
    isOpen,
    props: {
      styleName,
      onClose: close,
      onOpenFontManager,
    },
    open,
    close,
  };
}
